import { useEffect, useRef } from "react";
import { CheckCircle2, MapPin, Package, RefreshCw, Cloud } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * Live inventering panel (mobile-first).
 * Shows what has been counted at the location so far — re-fetched from the DB
 * after every scan — so warehouse staff can SEE that each scan is saved and that
 * the running quantities are correct (spot miscounts / missing items instantly).
 */
export function InventoryLivePanel({ location, items = [], loading, lastUpdate }) {
  const totalUnique = items.length;
  const totalQty = items.reduce((sum, p) => sum + (p.antal || 0), 0);
  const highlightRef = useRef(null);

  // Scroll the just-updated row into view on mobile
  useEffect(() => {
    if (lastUpdate && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [lastUpdate]);

  return (
    <div className="space-y-3" data-testid="inventory-live-panel">
      {/* Sticky running summary */}
      <Card className="border-blue-500/30 bg-blue-500/5 sticky top-2 z-10">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="h-5 w-5 text-blue-500 shrink-0" />
              <span className="font-mono font-bold text-lg truncate" data-testid="inventory-panel-location">
                {location}
              </span>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <p className="text-2xl font-bold leading-none" data-testid="inventory-panel-total-qty">{totalQty}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">st totalt</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold leading-none text-muted-foreground" data-testid="inventory-panel-unique">{totalUnique}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">unika</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-green-600">
            <Cloud className="h-3.5 w-3.5" />
            <span>Allt sparas automatiskt efter varje skanning</span>
            {loading && <RefreshCw className="h-3 w-3 animate-spin ml-1 text-muted-foreground" />}
          </div>
        </CardContent>
      </Card>

      {/* Last saved confirmation */}
      {lastUpdate && (
        <div
          className="flex items-center gap-2 rounded-lg bg-green-500/10 border border-green-500/30 px-3 py-2 text-sm"
          data-testid="inventory-last-saved"
        >
          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
          <span className="font-medium text-green-600 truncate">
            Sparat: {lastUpdate.artikelnummer}
          </span>
          {lastUpdate.newQty != null && (
            <Badge className="ml-auto bg-green-600 hover:bg-green-600 text-white shrink-0">
              nu {lastUpdate.newQty} st
            </Badge>
          )}
        </div>
      )}

      {/* Counted products list */}
      <Card>
        <CardContent className="p-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Package className="h-3.5 w-3.5" />
            Räknat på denna plats
          </p>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="inventory-panel-empty">
              Inget räknat än — skanna första produkten
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[45vh] overflow-y-auto" data-testid="inventory-panel-list">
              {items.map((p) => {
                const isJustUpdated = lastUpdate && p.ean === lastUpdate.ean;
                return (
                  <div
                    key={p.id || p.ean}
                    ref={isJustUpdated ? highlightRef : null}
                    className={cn(
                      "flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-colors",
                      isJustUpdated
                        ? "border-green-500/50 bg-green-500/10"
                        : "border-border bg-card"
                    )}
                    data-testid={`inventory-count-row-${p.ean}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{p.artikelnummer || p.ean}</p>
                      {p.produktnamn && (
                        <p className="text-xs text-muted-foreground truncate">{p.produktnamn}</p>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        "font-mono text-base shrink-0",
                        isJustUpdated && "border-green-500 text-green-600"
                      )}
                      data-testid={`inventory-count-qty-${p.ean}`}
                    >
                      {p.antal} st
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
